/**
 * TabBar — navigazione mobile fissa in basso, sempre visibile.
 *
 * Andrea 2026-05-23: ristrutturata a 5 tab principali. Le altre pagine
 * (admin, consultazione, settings) sono accessibili dal drawer hamburger
 * (cfr. `MobileDrawer.tsx`). Tutti gli utenti vedono i 5 tab sempre.
 */

import type React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { tr } from "../../i18n";

interface TabDef {
  to: string;
  label: string;
  icon: string;
  /** Predicate per evidenziare il tab attivo. */
  match: (pathname: string) => boolean;
}

// Andrea 2026-10-16: Home al centro, sollevata in oro e senza scritta; le
// altre quattro ai lati (Rose, Squadra | Classifica, Calendario).
const TABS: TabDef[] = [
  {
    to: "/rose",
    label: tr("Rose"),
    icon: "👥",
    match: (p) => p === "/rose",
  },
  {
    to: "/rosa",
    label: tr("Squadra"),
    icon: "⚽",
    match: (p) => p.startsWith("/rosa") || p.startsWith("/rinnovi") || p.startsWith("/formazione"),
  },
  {
    to: "/",
    label: tr("Home"),
    icon: "🏠",
    match: (p) => p === "/",
  },
  {
    to: "/lega",
    label: tr("Classifica"),
    icon: "🏆",
    match: (p) => p.startsWith("/lega"),
  },
  {
    to: "/giornata",
    label: tr("Calendario"),
    icon: "📅",
    match: (p) => p.startsWith("/giornata") || p.startsWith("/partita"),
  },
];

export function TabBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  /** Andrea 2026-05-23: navigazione "app-like" — il back button dal
   * sistema torna SEMPRE alla home dashboard `/`, non alla tab
   * precedente. Implementazione: prima reset history corrente a "/"
   * via replace, poi push del target. La history risultante avra'
   * sempre `/` come pagina precedente del target. */
  function navigateAppLike(to: string) {
    if (to === "/") {
      navigate("/", { replace: true });
      return;
    }
    navigate("/", { replace: true });
    navigate(to);
  }

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-primary-deep border-t border-primary/30 px-1 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2">
      <div className="max-w-md mx-auto grid grid-cols-5 gap-1 items-center">
        {TABS.map((t) => {
          const active = t.match(pathname);
          const vai = (e: React.MouseEvent) => {
            e.preventDefault();
            if (!active) navigateAppLike(t.to);
          };
          if (t.to === "/") {
            // Bottone tondo oro che sbuca sopra la barra (anello del colore
            // della barra per staccarlo dal contenuto), senza dicitura.
            return (
              <Link
                key={t.to}
                to={t.to}
                onClick={vai}
                aria-label={tr(t.label)}
                title={tr(t.label)}
                className="flex justify-center min-w-0"
              >
                <span
                  className={`w-[58px] h-[58px] -mt-[26px] rounded-full flex items-center justify-center text-[26px] leading-none transition ${
                    // Andrea 2026-10-16: oro pieno e bagliore solo in Home;
                    // altrove cerchio scuro col solo bordo oro.
                    active
                      ? "bg-accent shadow-[0_0_0_5px_rgb(var(--fs-primary-deep)),0_6px_18px_rgba(212,175,55,0.45)]"
                      : "bg-primary border-2 border-accent shadow-[0_0_0_5px_rgb(var(--fs-primary-deep))] hover:bg-primary/80"
                  }`}
                >
                  {t.icon}
                </span>
              </Link>
            );
          }
          return (
            <Link
              key={t.to}
              to={t.to}
              onClick={vai}
              className={`flex flex-col items-center gap-1 py-2 px-0 min-w-0 rounded-md transition ${
                active ? "text-accent" : "text-fg-muted hover:text-fg"
              }`}
            >
              <span
                className={`text-xl leading-none ${
                  active ? "drop-shadow-[0_0_8px_rgba(212,175,55,0.5)]" : ""
                }`}
              >
                {t.icon}
              </span>
              {/* 9px e tracking stretto, cosi' anche CALENDARIO e CLASSIFICA
                  stanno dentro la loro colonna. */}
              <span className="text-[9px] font-bold uppercase tracking-wide max-w-full truncate">
                {tr(t.label)}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
